import { useRef, useCallback, useMemo, useState, useEffect } from 'react';
import { VoipClient, loadBrowserPlatform } from '@pickit/voip';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';

// #style-inject:#style-inject
function styleInject(css, { insertAt } = {}) {
  if (typeof document === "undefined") return;
  const head = document.head || document.getElementsByTagName("head")[0];
  const style = document.createElement("style");
  style.type = "text/css";
  if (insertAt === "top") {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }
  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

// src/style.css
styleInject('.pkv-widget {\n  --pkv-bg: #0f172a;\n  --pkv-bg-elevated: #1e293b;\n  --pkv-text: #f1f5f9;\n  --pkv-muted: #94a3b8;\n  --pkv-accent: #3b82f6;\n  --pkv-accent-on: #ffffff;\n  --pkv-success: #10b981;\n  --pkv-warn: #f59e0b;\n  --pkv-danger: #ef4444;\n  --pkv-border: #334155;\n  --pkv-radius: 12px;\n  --pkv-radius-sm: 8px;\n  --pkv-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);\n  width: 320px;\n  font-family:\n    ui-sans-serif,\n    system-ui,\n    -apple-system,\n    "Segoe UI",\n    Roboto,\n    sans-serif;\n  font-size: 14px;\n  line-height: 1.4;\n  color: var(--pkv-text);\n  background: var(--pkv-bg);\n  border-radius: var(--pkv-radius);\n  box-shadow: var(--pkv-shadow);\n  overflow: hidden;\n  display: flex;\n  flex-direction: column;\n  transition: width 0.2s ease;\n  max-height: min(640px, calc(100vh - 110px));\n}\n.pkv-widget--wide {\n  width: min(460px, 90vw);\n}\n.pkv-widget--wide .pkv-caller-info {\n  max-height: 420px;\n}\n.pkv-widget * {\n  box-sizing: border-box;\n}\n.pkv-header {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: 0.5rem;\n  padding: 0.6rem 0.75rem;\n  background: var(--pkv-bg-elevated);\n  border-bottom: 1px solid var(--pkv-border);\n}\n.pkv-identity {\n  display: flex;\n  flex-direction: column;\n  gap: 0.1rem;\n  min-width: 0;\n}\n.pkv-identity__name {\n  font-weight: 600;\n}\n.pkv-identity__sub {\n  color: var(--pkv-muted);\n  font-size: 11px;\n}\n.pkv-status-toggle {\n  display: inline-flex;\n  background: rgba(255, 255, 255, 0.05);\n  border-radius: 999px;\n  padding: 2px;\n}\n.pkv-status-toggle button {\n  border: none;\n  background: transparent;\n  color: var(--pkv-muted);\n  font-size: 11px;\n  font-weight: 600;\n  padding: 4px 10px;\n  border-radius: 999px;\n  cursor: pointer;\n}\n.pkv-status-toggle button.is-active {\n  background: var(--pkv-success);\n  color: var(--pkv-accent-on);\n}\n.pkv-status-toggle button.is-active.is-away {\n  background: var(--pkv-muted);\n}\n.pkv-status-toggle button:disabled {\n  opacity: 0.5;\n  cursor: not-allowed;\n}\n.pkv-body {\n  display: flex;\n  flex-direction: column;\n  gap: 0.6rem;\n  padding: 0.75rem;\n  overflow-y: auto;\n  min-height: 0;\n}\n.pkv-pill {\n  display: inline-flex;\n  align-items: center;\n  gap: 4px;\n  font-size: 11px;\n  font-weight: 600;\n  padding: 2px 8px;\n  border-radius: 999px;\n  background: rgba(255, 255, 255, 0.06);\n  color: var(--pkv-muted);\n}\n.pkv-pill.is-success {\n  background: rgba(16, 185, 129, 0.15);\n  color: var(--pkv-success);\n}\n.pkv-pill.is-warn {\n  background: rgba(245, 158, 11, 0.15);\n  color: var(--pkv-warn);\n}\n.pkv-pill.is-danger {\n  background: rgba(239, 68, 68, 0.15);\n  color: var(--pkv-danger);\n}\n.pkv-pill::before {\n  content: "";\n  width: 6px;\n  height: 6px;\n  border-radius: 50%;\n  background: currentColor;\n}\n.pkv-affaire {\n  display: flex;\n  align-items: center;\n  gap: 8px;\n  padding: 8px 10px;\n  border-radius: var(--pkv-radius-sm);\n  background: rgba(245, 158, 11, 0.12);\n  border: 1px solid rgba(245, 158, 11, 0.45);\n}\n.pkv-affaire--none {\n  background: rgba(255, 255, 255, 0.04);\n  border: 1px dashed var(--pkv-border);\n}\n.pkv-affaire__label {\n  flex: 1;\n  min-width: 0;\n}\n.pkv-affaire__num {\n  font-size: 16px;\n  font-weight: 700;\n  color: var(--pkv-warn);\n}\n.pkv-affaire__hint {\n  font-size: 11px;\n  color: var(--pkv-muted);\n}\n.pkv-section {\n  display: flex;\n  flex-direction: column;\n  gap: 0.4rem;\n}\n.pkv-section__title {\n  font-size: 11px;\n  text-transform: uppercase;\n  letter-spacing: 0.05em;\n  color: var(--pkv-muted);\n  margin: 0;\n  padding: 0 2px;\n}\n.pkv-dial input {\n  width: 100%;\n  background: var(--pkv-bg-elevated);\n  border: 1px solid var(--pkv-border);\n  color: var(--pkv-text);\n  padding: 8px 10px;\n  border-radius: var(--pkv-radius-sm);\n  font-size: 14px;\n  font-family: inherit;\n  outline: none;\n}\n.pkv-dial input:focus {\n  border-color: var(--pkv-accent);\n}\n.pkv-dial-pad {\n  display: grid;\n  grid-template-columns: repeat(3, 1fr);\n  gap: 6px;\n  margin-top: 6px;\n}\n.pkv-dial-pad button {\n  background: var(--pkv-bg-elevated);\n  border: 1px solid var(--pkv-border);\n  color: var(--pkv-text);\n  padding: 8px 0;\n  border-radius: var(--pkv-radius-sm);\n  font-size: 16px;\n  cursor: pointer;\n  transition: background 0.1s;\n}\n.pkv-dial-pad button:hover:not(:disabled) {\n  background: rgba(255, 255, 255, 0.08);\n}\n.pkv-dial-pad button:disabled {\n  opacity: 0.4;\n  cursor: not-allowed;\n}\n.pkv-dtmf {\n  margin-top: 8px;\n}\n.pkv-dtmf__echo {\n  font-family:\n    ui-monospace,\n    SFMono-Regular,\n    Menlo,\n    monospace;\n  font-size: 15px;\n  letter-spacing: 2px;\n  text-align: center;\n  padding: 4px 8px;\n  min-height: 24px;\n  margin-bottom: 6px;\n  border-radius: var(--pkv-radius-sm);\n  background: var(--pkv-bg-elevated);\n  border: 1px solid var(--pkv-border);\n  overflow-x: auto;\n  white-space: nowrap;\n}\n.pkv-dtmf__pad {\n  display: grid;\n  grid-template-columns: repeat(3, 1fr);\n  gap: 6px;\n}\n.pkv-dtmf__key {\n  background: var(--pkv-bg-elevated);\n  border: 1px solid var(--pkv-border);\n  color: var(--pkv-text);\n  padding: 10px 0;\n  border-radius: var(--pkv-radius-sm);\n  font-size: 17px;\n  font-weight: 600;\n  cursor: pointer;\n  transition: background 0.1s;\n}\n.pkv-dtmf__key:hover {\n  background: rgba(255, 255, 255, 0.08);\n}\n.pkv-dtmf__key:active {\n  background: var(--pkv-accent);\n  color: var(--pkv-accent-on);\n}\n.pkv-btn {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  gap: 6px;\n  border: none;\n  border-radius: var(--pkv-radius-sm);\n  font-family: inherit;\n  font-size: 14px;\n  font-weight: 600;\n  padding: 9px 14px;\n  cursor: pointer;\n  transition: opacity 0.1s, background 0.1s;\n}\n.pkv-btn:disabled {\n  opacity: 0.4;\n  cursor: not-allowed;\n}\n.pkv-btn--primary {\n  background: var(--pkv-accent);\n  color: var(--pkv-accent-on);\n}\n.pkv-btn--danger {\n  background: var(--pkv-danger);\n  color: var(--pkv-accent-on);\n}\n.pkv-btn--ghost {\n  background: rgba(255, 255, 255, 0.06);\n  color: var(--pkv-text);\n}\n.pkv-btn--block {\n  width: 100%;\n}\n.pkv-presence {\n  display: flex;\n  flex-direction: column;\n  gap: 4px;\n}\n.pkv-presence__row {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: 6px;\n  padding: 6px 8px;\n  background: var(--pkv-bg-elevated);\n  border-radius: var(--pkv-radius-sm);\n  font-size: 13px;\n}\n.pkv-presence__name {\n  font-weight: 600;\n}\n.pkv-presence__row .pkv-btn {\n  padding: 4px 10px;\n  font-size: 12px;\n}\n.pkv-incoming,\n.pkv-active {\n  display: flex;\n  flex-direction: column;\n  gap: 0.6rem;\n  padding: 0.85rem 0.75rem;\n  background: var(--pkv-bg-elevated);\n  border: 1px solid var(--pkv-border);\n  border-radius: var(--pkv-radius-sm);\n}\n.pkv-incoming {\n  border-color: var(--pkv-accent);\n  animation: pkv-pulse 1.5s ease-in-out infinite;\n}\n@keyframes pkv-pulse {\n  0%, 100% {\n    box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.45);\n  }\n  50% {\n    box-shadow: 0 0 0 6px rgba(59, 130, 246, 0);\n  }\n}\n.pkv-active__caller,\n.pkv-incoming__caller {\n  font-weight: 600;\n  font-size: 15px;\n  text-overflow: ellipsis;\n  overflow: hidden;\n  white-space: nowrap;\n}\n.pkv-active__timer {\n  color: var(--pkv-muted);\n  font-variant-numeric: tabular-nums;\n  font-size: 13px;\n}\n.pkv-active__timer--ringing {\n  color: var(--pkv-accent);\n  animation: pkv-ring-pulse 1.5s ease-in-out infinite;\n}\n@keyframes pkv-ring-pulse {\n  0%, 100% {\n    opacity: 0.55;\n  }\n  50% {\n    opacity: 1;\n  }\n}\n.pkv-active__timer--ended {\n  color: var(--pkv-success);\n  font-weight: 600;\n}\n.pkv-active__controls {\n  display: grid;\n  grid-template-columns: repeat(3, 1fr);\n  gap: 6px;\n}\n.pkv-active__controls .pkv-btn {\n  padding: 8px;\n  font-size: 13px;\n}\n.pkv-muted-text {\n  color: var(--pkv-muted);\n  font-size: 12px;\n}\n.pkv-error {\n  color: var(--pkv-danger);\n  font-size: 12px;\n  padding: 4px 0;\n}\n.pkv-notice {\n  color: var(--pkv-muted);\n  font-size: 12px;\n  padding: 4px 0;\n}\n.pkv-caller-info {\n  display: flex;\n  flex-direction: column;\n  gap: 6px;\n  padding: 8px;\n  background: rgba(0, 0, 0, 0.18);\n  border-radius: var(--pkv-radius-sm);\n  font-size: 12px;\n  max-height: 280px;\n  overflow-y: auto;\n}\n.pkv-caller-match {\n  display: flex;\n  flex-direction: column;\n  gap: 4px;\n}\n.pkv-caller-match__head {\n  font-size: 13px;\n}\n.pkv-caller-affaires {\n  list-style: none;\n  margin: 0;\n  padding: 0;\n  display: flex;\n  flex-direction: column;\n  gap: 4px;\n}\n.pkv-caller-affaire {\n  background: rgba(255, 255, 255, 0.05);\n  border-radius: var(--pkv-radius-sm);\n  padding: 4px 6px;\n}\n.pkv-caller-affaire__line {\n  display: flex;\n  align-items: center;\n  gap: 4px;\n  flex-wrap: wrap;\n}\n.pkv-caller-affaire__meta {\n  color: var(--pkv-muted);\n  font-size: 11px;\n  margin-top: 2px;\n}\n.pkv-caller-note {\n  padding-top: 6px;\n  border-top: 1px solid rgba(255, 255, 255, 0.08);\n  display: flex;\n  flex-direction: column;\n  gap: 3px;\n}\n.pkv-caller-note__body {\n  background: rgba(250, 204, 21, 0.12);\n  border-left: 2px solid rgba(250, 204, 21, 0.6);\n  padding: 4px 6px;\n  border-radius: 3px;\n  white-space: pre-wrap;\n}\n.pkv-caller-note__meta {\n  color: var(--pkv-muted);\n  font-size: 10px;\n}\n.pkv-caller-note__textarea {\n  font: inherit;\n  color: var(--pkv-text);\n  background: rgba(0, 0, 0, 0.25);\n  border: 1px solid var(--pkv-border);\n  border-radius: var(--pkv-radius-sm);\n  padding: 4px 6px;\n  resize: vertical;\n  width: 100%;\n  font-size: 12px;\n}\n.pkv-caller-note__actions {\n  display: flex;\n  gap: 4px;\n}\n.pkv-flow-switcher {\n  display: flex;\n  flex-direction: column;\n  gap: 6px;\n}\n.pkv-flow-switcher__head {\n  display: flex;\n  align-items: center;\n  gap: 6px;\n  justify-content: space-between;\n}\n.pkv-flow-switcher__select {\n  font: inherit;\n  color: var(--pkv-text);\n  background: var(--pkv-bg-elevated);\n  border: 1px solid var(--pkv-border);\n  border-radius: var(--pkv-radius-sm);\n  padding: 6px 8px;\n  width: 100%;\n  font-size: 13px;\n}\n.pkv-flow-switcher__list {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 4px;\n}\n.pkv-flow-switcher__pill {\n  font: inherit;\n  font-size: 11.5px;\n  font-weight: 500;\n  padding: 4px 10px;\n  border-radius: 999px;\n  border: 1px solid var(--pkv-border);\n  background: var(--pkv-bg-elevated);\n  color: var(--pkv-text);\n  cursor: pointer;\n  transition:\n    background 0.12s,\n    border-color 0.12s,\n    color 0.12s,\n    transform 0.05s;\n}\n.pkv-flow-switcher__pill:hover:not(:disabled) {\n  background: rgba(59, 130, 246, 0.16);\n  border-color: var(--pkv-accent);\n}\n.pkv-flow-switcher__pill:active:not(:disabled) {\n  transform: translateY(1px);\n}\n.pkv-flow-switcher__pill:disabled {\n  cursor: default;\n  opacity: 0.7;\n}\n.pkv-flow-switcher__pill--active {\n  background: var(--pkv-accent);\n  border-color: var(--pkv-accent);\n  color: var(--pkv-accent-on);\n}\n.pkv-flow-switcher__pill--active:disabled {\n  opacity: 1;\n}\n.pkv-transfer-overlay {\n  position: absolute;\n  inset: 0;\n  background: rgba(15, 23, 42, 0.95);\n  display: flex;\n  flex-direction: column;\n  gap: 6px;\n  padding: 0.75rem;\n  overflow-y: auto;\n}\n.pkv-widget__container {\n  position: relative;\n}\n.pkv-repondeur {\n  display: flex;\n  flex-direction: column;\n  gap: 0.6rem;\n}\n.pkv-repondeur__header {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n}\n.pkv-repondeur__slot {\n  display: flex;\n  flex-direction: column;\n  gap: 0.4rem;\n  padding: 0.6rem;\n  background: var(--pkv-bg-elevated);\n  border: 1px solid var(--pkv-border);\n  border-radius: var(--pkv-radius-sm);\n}\n.pkv-repondeur__slot-head {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: 6px;\n}\n.pkv-repondeur__row {\n  display: flex;\n  align-items: center;\n  gap: 6px;\n}\n.pkv-repondeur__slot select {\n  width: 100%;\n  background: var(--pkv-bg);\n  color: var(--pkv-text);\n  border: 1px solid var(--pkv-border);\n  border-radius: var(--pkv-radius-sm);\n  padding: 6px 8px;\n  font-family: inherit;\n  font-size: 13px;\n}\n.pkv-sync-edit {\n  display: inline-flex;\n  flex-direction: column;\n  gap: 4px;\n  margin-left: 6px;\n  vertical-align: top;\n}\n.pkv-sync-results {\n  list-style: none;\n  margin: 0;\n  padding: 4px;\n  background: var(--pkv-bg-elevated);\n  border: 1px solid var(--pkv-border);\n  border-radius: var(--pkv-radius-sm);\n  max-height: 200px;\n  overflow-y: auto;\n  min-width: 240px;\n}\n.pkv-sync-results__item {\n  display: flex;\n  flex-direction: column;\n  align-items: flex-start;\n  width: 100%;\n  border: none;\n  background: transparent;\n  color: var(--pkv-text);\n  text-align: left;\n  padding: 5px 8px;\n  border-radius: 6px;\n  cursor: pointer;\n  font: inherit;\n}\n.pkv-sync-results__item:hover,\n.pkv-sync-results__item:focus-visible {\n  background: rgba(255, 255, 255, 0.08);\n  outline: none;\n}\n.pkv-sync-results__main {\n  font-size: 12.5px;\n  font-weight: 600;\n}\n.pkv-sync-results__sub {\n  font-size: 11.5px;\n  color: var(--pkv-muted);\n}\n.pkv-sync-results__empty {\n  font-size: 11.5px;\n  color: var(--pkv-muted);\n  padding: 5px 8px;\n}\n');

// src/accept-call.ts
var ANSWER_TOO_LATE = "Trop tard \u2014 l'appelant a raccroch\xE9 ou l'appel est pass\xE9 au coll\xE8gue suivant.";
function micErrorMessage(err) {
  const e = err;
  if (e?.name === "NotAllowedError")
    return "Acc\xE8s au micro refus\xE9. Autorisez le micro dans les r\xE9glages du navigateur.";
  if (e?.name === "NotFoundError") return "Aucun micro d\xE9tect\xE9.";
  return `Erreur micro : ${e?.message ?? String(err)}`;
}
function release(stream) {
  for (const t of stream?.getTracks() ?? []) {
    try {
      t.stop();
    } catch {
    }
  }
}
async function acceptCall(deps) {
  const t0 = Date.now();
  const trace = (t) => {
    try {
      deps.onTrace?.(t);
    } catch {
    }
  };
  trace({ kind: "attempt", at: new Date(t0).toISOString() });
  const outcome = await repondre(deps);
  const elapsedMs = Date.now() - t0;
  trace(
    outcome.ok ? { kind: "ok", at: (/* @__PURE__ */ new Date()).toISOString(), elapsedMs } : { kind: "failed", at: (/* @__PURE__ */ new Date()).toISOString(), reason: outcome.kind, message: outcome.message, elapsedMs }
  );
  return outcome;
}
async function repondre(deps) {
  const { call } = deps;
  if (call.state === "ended") return { ok: false, kind: "gone", message: ANSWER_TOO_LATE };
  const icePromise = deps.getIceServers().catch(() => []);
  let stream;
  try {
    stream = await deps.getMedia();
  } catch (mediaErr) {
    void icePromise.catch(() => void 0);
    if (call.state !== "ended") {
      try {
        call.reject({ status: 480, reason: "Mic Unavailable" });
      } catch {
      }
    }
    return { ok: false, kind: "mic", message: micErrorMessage(mediaErr) };
  }
  const iceServers = await icePromise;
  if (call.state === "ended") {
    release(stream);
    return { ok: false, kind: "gone", message: ANSWER_TOO_LATE };
  }
  try {
    call.accept({ mediaStream: stream, iceServers });
    return { ok: true };
  } catch (err) {
    release(stream);
    const msg = err.message ?? String(err);
    if (msg.includes("already-ended")) return { ok: false, kind: "gone", message: ANSWER_TOO_LATE };
    return { ok: false, kind: "failed", message: `D\xE9croch\xE9 impossible : ${msg}` };
  }
}

// src/ringer.ts
var Ringer = class {
  ctx = null;
  timer = null;
  osc1 = null;
  osc2 = null;
  gain = null;
  playing = false;
  unlock() {
    if (this.ctx) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    try {
      this.ctx = new Ctx();
      const buf = this.ctx.createBuffer(1, 1, 22050);
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      src.connect(this.ctx.destination);
      src.start(0);
      void this.ctx.resume();
    } catch {
    }
  }
  start() {
    if (this.playing) return;
    this.playing = true;
    if (!this.ctx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      this.ctx = new Ctx();
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    let on = true;
    const tick = () => {
      if (!this.playing) return;
      if (on) this.startTone();
      else this.stopTone();
      on = !on;
    };
    tick();
    this.timer = setInterval(tick, 1500);
  }
  stop() {
    if (!this.playing) return;
    this.playing = false;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.stopTone();
  }
  startTone() {
    if (!this.ctx) return;
    this.stopTone();
    const ctx = this.ctx;
    this.gain = ctx.createGain();
    this.gain.gain.setValueAtTime(0.15, ctx.currentTime);
    this.osc1 = ctx.createOscillator();
    this.osc1.type = "sine";
    this.osc1.frequency.value = 440;
    this.osc2 = ctx.createOscillator();
    this.osc2.type = "sine";
    this.osc2.frequency.value = 480;
    this.osc1.connect(this.gain);
    this.osc2.connect(this.gain);
    this.gain.connect(ctx.destination);
    this.osc1.start();
    this.osc2.start();
  }
  stopTone() {
    for (const o of [this.osc1, this.osc2]) {
      if (!o) continue;
      try {
        o.stop();
      } catch {
      }
      o.disconnect();
    }
    this.osc1 = null;
    this.osc2 = null;
    if (this.gain) {
      this.gain.disconnect();
      this.gain = null;
    }
  }
  /** Short descending double-beep played once when a call ends.
   *  ~250 ms total. Distinct from the ringing tone so the user
   *  can't confuse "still ringing" with "the call just ended". */
  playEndTone() {
    if (!this.ctx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      this.ctx = new Ctx();
    }
    if (this.ctx.state === "suspended") void this.ctx.resume();
    const ctx = this.ctx;
    const now = ctx.currentTime;
    const playBeep = (start, dur, freq) => {
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.18, start);
      g.gain.exponentialRampToValueAtTime(1e-3, start + dur);
      const o = ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = freq;
      o.connect(g);
      g.connect(ctx.destination);
      o.start(start);
      o.stop(start + dur + 0.02);
    };
    playBeep(now, 0.1, 600);
    playBeep(now + 0.12, 0.12, 420);
  }
};
var ringer = new Ringer();
var _platformLoaded = false;
function ensurePlatform() {
  if (_platformLoaded) return;
  loadBrowserPlatform();
  _platformLoaded = true;
}
var CALL_ENDED_LINGER_MS = 3e3;
function isInternalCaller(remoteIdentity) {
  const user = remoteIdentity.replace(/^sip:/i, "").split("@")[0] ?? "";
  return /^[a-z]/i.test(user);
}
function AffaireBanner(props) {
  if (!props.affaire) {
    return /* @__PURE__ */ jsx("div", { className: "pkv-affaire pkv-affaire--none", children: /* @__PURE__ */ jsx("div", { className: "pkv-affaire__label", children: /* @__PURE__ */ jsx("div", { className: "pkv-affaire__hint", children: "Aucune affaire associ\xE9e \u2014 l'appelant n'a pas saisi de num\xE9ro" }) }) });
  }
  return /* @__PURE__ */ jsxs("div", { className: "pkv-affaire", children: [
    /* @__PURE__ */ jsxs("div", { className: "pkv-affaire__label", children: [
      /* @__PURE__ */ jsxs("div", { className: "pkv-affaire__num", children: [
        "Affaire ",
        props.affaire
      ] }),
      /* @__PURE__ */ jsx("div", { className: "pkv-affaire__hint", children: "L'appel sera associ\xE9 \xE0 cette affaire" })
    ] }),
    props.onOpen && /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: "pkv-btn pkv-btn--ghost",
        style: { padding: "4px 10px", fontSize: 12, flexShrink: 0 },
        onClick: props.onOpen,
        children: "Ouvrir \u2197"
      }
    )
  ] });
}
function PhoneGlyph({ size = 14, mr = false }) {
  return /* @__PURE__ */ jsx(
    "svg",
    {
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: "2.2",
      strokeLinecap: "round",
      strokeLinejoin: "round",
      "aria-hidden": "true",
      style: { verticalAlign: "-2px", marginRight: mr ? 6 : 0 },
      children: /* @__PURE__ */ jsx("path", { d: "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" })
    }
  );
}
function parseDisplayName(displayName) {
  if (!displayName) return { affaire: null, remoteName: null };
  const m = displayName.match(/^Affaire (\d+)\s*-\s*(.*)$/);
  if (!m) return { affaire: null, remoteName: displayName };
  return { affaire: m[1] ?? null, remoteName: (m[2] ?? "").trim() || null };
}
function VoipWidget(props) {
  ensurePlatform();
  const incomingSinceRef = useRef(/* @__PURE__ */ new WeakMap());
  const { me, handlers } = props;
  const log = useCallback(
    (level, msg, fields) => {
      handlers.log?.(level, msg, fields);
    },
    [handlers]
  );
  const freshIceServersRef = useRef(async () => me.iceServers);
  freshIceServersRef.current = async () => {
    if (handlers.refreshIceServers) {
      try {
        const fresh = await handlers.refreshIceServers();
        if (fresh && fresh.length > 0) return fresh;
        log("warn", "refreshIceServers returned empty; using cached ICE");
      } catch (e) {
        log("warn", "refreshIceServers failed; using cached ICE", { error: String(e) });
      }
    }
    return me.iceServers;
  };
  const client = useMemo(() => {
    return new VoipClient({
      sip: {
        wsServer: me.sip.wsServer,
        user: me.sip.user,
        password: me.sip.password,
        domain: me.sip.domain,
        displayName: me.sip.displayName
      },
      iceServers: me.iceServers,
      // Outbound calls resolve fresh ICE (new TURN cred) via this provider.
      iceServersProvider: () => freshIceServersRef.current(),
      qualityIntervalMs: 0,
      reconnect: { enabled: true, backoffMs: 2e3, maxAttempts: 10 },
      logLevel: "warn"
    });
  }, [me.agent.id]);
  const [connectionState, setConnectionState] = useState("idle");
  const [registered, setRegistered] = useState(false);
  const [myStatus, setMyStatus] = useState(me.agent.status);
  const [statusBusy, setStatusBusy] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const [incomingCalls, setIncomingCalls] = useState([]);
  const [number, setNumber] = useState(props.defaultDialTarget ?? "");
  const [dialOpen, setDialOpen] = useState(false);
  const [dialing, setDialing] = useState(false);
  const [syncTarget, setSyncTarget] = useState(null);
  const [syncEdit, setSyncEdit] = useState(false);
  const [syncNum, setSyncNum] = useState("");
  const [syncHits, setSyncHits] = useState([]);
  const [syncSearching, setSyncSearching] = useState(false);
  const syncSearchSeq = useRef(0);
  const searchSyncTargets = handlers.searchSyncTargets;
  useEffect(() => {
    if (!syncEdit || !searchSyncTargets) return void 0;
    const q = syncNum.trim();
    if (q.length < 2) {
      setSyncHits([]);
      return void 0;
    }
    const seq = ++syncSearchSeq.current;
    setSyncSearching(true);
    const t = setTimeout(() => {
      void searchSyncTargets(q).then((hits) => {
        if (seq !== syncSearchSeq.current) return;
        setSyncHits(hits.slice(0, 8));
      }).catch(() => {
        if (seq === syncSearchSeq.current) setSyncHits([]);
      }).finally(() => {
        if (seq === syncSearchSeq.current) setSyncSearching(false);
      });
    }, 250);
    return () => clearTimeout(t);
  }, [syncEdit, syncNum, searchSyncTargets]);
  const [presence, setPresence] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  useEffect(() => {
    if (!notice) return void 0;
    const t = setTimeout(() => setNotice(null), 8e3);
    return () => clearTimeout(t);
  }, [notice]);
  const [transferOpen, setTransferOpen] = useState(false);
  const [dtmfOpen, setDtmfOpen] = useState(false);
  const [dtmfSent, setDtmfSent] = useState("");
  const [tick, setTick] = useState(0);
  const audioRef = useRef(null);
  const devicesRef = useRef({});
  useEffect(() => {
    const onSet = (e) => {
      const d = e.detail ?? {};
      devicesRef.current = { input: d.inputId || void 0, output: d.outputId || void 0 };
      const el = audioRef.current;
      if (el?.setSinkId) void el.setSinkId(d.outputId || "").catch(() => void 0);
    };
    window.addEventListener("pkv:set-devices", onSet);
    window.dispatchEvent(new CustomEvent("pkv:devices-ready"));
    return () => window.removeEventListener("pkv:set-devices", onSet);
  }, []);
  const micConstraints = () => ({
    audio: devicesRef.current.input ? { deviceId: { ideal: devicesRef.current.input } } : true
  });
  const [flows, setFlows] = useState(null);
  const [overrideFlowId, setOverrideFlowId] = useState(null);
  const [flowSwitchBusy, setFlowSwitchBusy] = useState(false);
  const [callerInfo, setCallerInfo] = useState({});
  const [noteDrafts, setNoteDrafts] = useState({});
  const [noteSaving, setNoteSaving] = useState({});
  useEffect(() => {
    const offState = client.on("connection-state", setConnectionState);
    const offRegistered = client.on("registered", () => {
      setRegistered(true);
      setError(null);
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        void Notification.requestPermission().catch(() => void 0);
      }
    });
    const offUnregistered = client.on("unregistered", () => setRegistered(false));
    const offRegFailed = client.on("registration-failed", (e) => {
      setRegistered(false);
      setError(`registration failed: ${e.message}`);
    });
    const offError = client.on("error", (e) => setError(`${e.code}: ${e.message}`));
    const offIncoming = client.on("incoming-call", (call) => {
      if (phaseRef.current === "active" && !props.callWaiting && !isInternalCaller(call.remoteIdentity)) {
        try {
          call.reject({ status: 486, reason: "Busy Here" });
        } catch {
        }
        return;
      }
      incomingSinceRef.current.set(call, Date.now());
      setIncomingCalls((cur) => [...cur, call]);
      setNotice(null);
      ringer.start();
      const prevTitle = document.title;
      document.title = `\u{1F4DE} ${call.remoteIdentity} \u2014 appel entrant`;
      let notif = null;
      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        try {
          notif = new Notification("Appel entrant", {
            body: call.remoteIdentity,
            tag: `pkv-${call.id}`,
            requireInteraction: true
          });
          notif.onclick = () => {
            window.focus();
            try {
              window.dispatchEvent(new CustomEvent("pkv:focus-widget"));
            } catch {
            }
            notif?.close();
          };
        } catch {
        }
      }
      const offEnded = call.on("ended", () => {
        setIncomingCalls((cur) => {
          const next = cur.filter((c) => c !== call);
          if (next.length === 0) {
            ringer.stop();
            document.title = prevTitle;
          }
          return next;
        });
        notif?.close();
        offEnded();
      });
    });
    void (async () => {
      try {
        await client.connect();
        await client.register();
      } catch (err) {
        log("error", `boot: ${err.message}`);
      }
    })();
    const primeOnce = () => {
      ringer.unlock();
      document.removeEventListener("click", primeOnce);
      document.removeEventListener("keydown", primeOnce);
    };
    document.addEventListener("click", primeOnce, { once: true });
    document.addEventListener("keydown", primeOnce, { once: true });
    return () => {
      offState();
      offRegistered();
      offUnregistered();
      offRegFailed();
      offIncoming();
      offError();
      void client.disconnect();
    };
  }, [client, log]);
  useEffect(() => {
    const nudge = () => {
      const st = client.getState();
      if (st === "disconnected" || st === "failed") {
        void client.connect().then(() => client.register()).catch(() => void 0);
      }
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") nudge();
    };
    window.addEventListener("online", nudge);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", nudge);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [client]);
  useEffect(() => {
    const off = handlers.subscribePresence(setPresence);
    return () => off();
  }, [handlers]);
  const listFlows = handlers.listFlows;
  const getActiveOverride = handlers.getActiveOverride;
  useEffect(() => {
    if (!listFlows && !getActiveOverride) return;
    let cancelled = false;
    void Promise.allSettled([
      listFlows ? listFlows() : Promise.resolve([]),
      getActiveOverride ? getActiveOverride() : Promise.resolve(null)
    ]).then(([fRes, oRes]) => {
      if (cancelled) return;
      if (fRes.status === "fulfilled") setFlows(fRes.value);
      if (oRes.status === "fulfilled") setOverrideFlowId(oRes.value);
    });
    return () => {
      cancelled = true;
    };
  }, [listFlows, getActiveOverride]);
  async function applyOverride(nextId) {
    if (!handlers.setActiveOverride) return;
    setFlowSwitchBusy(true);
    try {
      await handlers.setActiveOverride(nextId);
      setOverrideFlowId(nextId);
    } catch (err) {
      setError(`override: ${err.message}`);
    } finally {
      setFlowSwitchBusy(false);
    }
  }
  const lookupCaller = handlers.lookupCaller;
  const getPhoneNote = handlers.getPhoneNote;
  useEffect(() => {
    if (!lookupCaller && !getPhoneNote) return;
    let cancelled = false;
    for (const call of incomingCalls) {
      if (callerInfo[call.id]) continue;
      setCallerInfo((cur) => ({
        ...cur,
        [call.id]: { loading: true, lookup: null, note: null, error: null }
      }));
      const num = call.remoteIdentity;
      void Promise.allSettled([
        lookupCaller ? lookupCaller(num) : Promise.resolve(null),
        getPhoneNote ? getPhoneNote(num) : Promise.resolve(null)
      ]).then(([lkRes, nRes]) => {
        if (cancelled) return;
        setCallerInfo((cur) => ({
          ...cur,
          [call.id]: {
            loading: false,
            lookup: lkRes.status === "fulfilled" ? lkRes.value : null,
            note: nRes.status === "fulfilled" ? nRes.value : null,
            error: lkRes.status === "rejected" ? String(lkRes.reason) : null
          }
        }));
      });
    }
    setCallerInfo((cur) => {
      const ids = new Set(incomingCalls.map((c) => c.id));
      if (activeCall) ids.add(activeCall.call.id);
      const next = {};
      for (const [k, v] of Object.entries(cur)) if (ids.has(k)) next[k] = v;
      return next;
    });
    return () => {
      cancelled = true;
    };
  }, [incomingCalls, lookupCaller, getPhoneNote, activeCall]);
  const dialToRef = useRef(() => Promise.resolve());
  dialToRef.current = dialTo;
  const registeredRef = useRef(registered);
  registeredRef.current = registered;
  const phaseRef = useRef("idle");
  const queuedDialRef = useRef(null);
  useEffect(() => {
    if (!registered) return;
    const q = queuedDialRef.current;
    queuedDialRef.current = null;
    if (q && Date.now() - q.at < 2e4) {
      setNotice(null);
      void dialToRef.current(q.number);
    }
  }, [registered]);
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail;
      if (!detail) return;
      const num = detail.number?.toString().trim();
      if (!num) return;
      if (phaseRef.current !== "idle" || !registeredRef.current) {
        detail.handled = true;
        try {
          window.dispatchEvent(new CustomEvent("pkv:focus-widget"));
        } catch {
        }
        setError(null);
        if (phaseRef.current !== "idle") {
          setNotice("Un appel est d\xE9j\xE0 en cours ou sonne \u2014 terminez-le avant d'en lancer un autre.");
        } else {
          queuedDialRef.current = { number: num, at: Date.now() };
          setNumber(num);
          setDialOpen(true);
          setNotice("Connexion au t\xE9l\xE9phone en cours\u2026 l'appel partira automatiquement.");
        }
        return;
      }
      detail.handled = true;
      setNumber(num);
      setDialOpen(true);
      try {
        window.dispatchEvent(new CustomEvent("pkv:focus-widget"));
      } catch {
      }
      void dialToRef.current(num);
    };
    window.addEventListener("pkv:dial", handler);
    return () => window.removeEventListener("pkv:dial", handler);
  }, []);
  const acceptRef = useRef(async () => {
  });
  const rejectRef = useRef(() => {
  });
  const incomingRef = useRef([]);
  acceptRef.current = accept;
  rejectRef.current = reject;
  incomingRef.current = incomingCalls;
  useEffect(() => {
    const onAnswer = () => {
      const c = incomingRef.current[0];
      if (c) void acceptRef.current(c);
    };
    const onDecline = () => {
      const c = incomingRef.current[0];
      if (c) rejectRef.current(c);
    };
    window.addEventListener("pkv:answer", onAnswer);
    window.addEventListener("pkv:decline", onDecline);
    return () => {
      window.removeEventListener("pkv:answer", onAnswer);
      window.removeEventListener("pkv:decline", onDecline);
    };
  }, []);
  useEffect(() => {
    window.__pickitVoipReady = registered;
    return () => {
      window.__pickitVoipReady = false;
    };
  }, [registered]);
  const liveCallsRef = useRef({ active: null, incoming: [] });
  liveCallsRef.current = { active: activeCall?.call ?? null, incoming: incomingCalls };
  useEffect(() => {
    const teardown = () => {
      const { active, incoming } = liveCallsRef.current;
      try {
        active?.hangup();
      } catch {
      }
      for (const c of incoming) {
        try {
          c.reject({ status: 486, reason: "Busy Here" });
        } catch {
        }
      }
    };
    window.addEventListener("pagehide", teardown);
    window.addEventListener("beforeunload", teardown);
    return () => {
      window.removeEventListener("pagehide", teardown);
      window.removeEventListener("beforeunload", teardown);
    };
  }, []);
  const getSyncTarget = handlers.getSyncTarget;
  const activeCallId = activeCall?.call.id ?? null;
  useEffect(() => {
    setSyncTarget(null);
    setSyncEdit(false);
    setSyncNum("");
    if (!activeCall || !getSyncTarget) return void 0;
    let on = true;
    void getSyncTarget({
      callerNumber: activeCall.remoteIdentity ?? null,
      ivrAffaire: activeCall.affaire ?? null,
      sipCallId: activeCall.call.sipCallId ?? null
    }).then((t) => {
      if (on) setSyncTarget(t);
    }).catch(() => {
    });
    return () => {
      on = false;
    };
  }, [activeCallId, getSyncTarget]);
  const applySyncTarget = (affaireNumber) => {
    const ac = activeCall;
    if (!ac) return;
    void handlers.onSetSyncTarget?.({ callerNumber: ac.remoteIdentity ?? null, sipCallId: ac.call.sipCallId ?? null }, affaireNumber).then((t) => {
      setSyncTarget(t);
      setSyncEdit(false);
      setSyncNum("");
      setSyncHits([]);
    }).catch(() => setSyncEdit(false));
  };
  useEffect(() => {
    if (!activeCall) return void 0;
    const id = setInterval(() => setTick((n) => n + 1), 1e3);
    return () => clearInterval(id);
  }, [activeCall]);
  const phase = activeCall ? "active" : incomingCalls.length > 0 ? "incoming" : dialing ? "dialing" : "idle";
  phaseRef.current = phase;
  const firstIncoming = incomingCalls[0] ?? null;
  useEffect(() => {
    const inc = firstIncoming ? (() => {
      const parsed = parseDisplayName(firstIncoming.displayName);
      return {
        number: firstIncoming.remoteIdentity,
        remoteName: parsed.remoteName ?? void 0,
        affaire: parsed.affaire ?? void 0
      };
    })() : null;
    try {
      window.dispatchEvent(
        new CustomEvent("pkv:phase", {
          detail: { phase, status: myStatus, registered, incoming: inc }
        })
      );
    } catch {
    }
  }, [phase, myStatus, registered, firstIncoming]);
  function wireActive(call) {
    const { affaire, remoteName } = parseDisplayName(call.displayName);
    const info = {
      call,
      remoteIdentity: call.remoteIdentity,
      remoteName,
      affaire,
      startedAt: null,
      endedAt: null,
      isMuted: false,
      isOnHold: false
    };
    call.on("ringing", () => ringer.start());
    call.on("connected", () => {
      ringer.stop();
      setActiveCall((cur) => cur ? { ...cur, startedAt: Date.now() } : cur);
    });
    call.on("ended", () => {
      ringer.stop();
      ringer.playEndTone();
      const endAt = Date.now();
      setActiveCall(
        (cur) => cur?.call === call ? { ...cur, endedAt: endAt } : cur
      );
      setTransferOpen(false);
      setTimeout(() => {
        setActiveCall((cur) => cur?.call === call ? null : cur);
      }, CALL_ENDED_LINGER_MS);
    });
    call.on("remote-track", (track) => {
      if (audioRef.current) audioRef.current.srcObject = new MediaStream([track]);
    });
    return info;
  }
  async function setStatus(next) {
    if (statusBusy || next === myStatus) return;
    setStatusBusy(true);
    try {
      await handlers.onSetStatus(next);
      setMyStatus(next);
      setError(null);
    } catch (err) {
      setError(`status: ${err.message}`);
    } finally {
      setStatusBusy(false);
    }
  }
  const dialingRef = useRef(false);
  const cancelDialRef = useRef(false);
  const pendingDialRef = useRef(null);
  function cancelDialing() {
    cancelDialRef.current = true;
    try {
      pendingDialRef.current?.hangup();
    } catch {
    }
  }
  async function dialTo(target) {
    if (!target.trim()) return;
    if (dialingRef.current || activeCall) {
      setNotice("Un appel est d\xE9j\xE0 en cours \u2014 terminez-le avant d'en lancer un autre.");
      return;
    }
    if (!registered) {
      setNotice("T\xE9l\xE9phone non enregistr\xE9 \u2014 connexion en cours, r\xE9essayez dans quelques secondes.");
      return;
    }
    dialingRef.current = true;
    cancelDialRef.current = false;
    setDialing(true);
    const release2 = () => {
      dialingRef.current = false;
      pendingDialRef.current = null;
      setDialing(false);
    };
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(micConstraints());
    } catch (mediaErr) {
      const e = mediaErr;
      const reason = e.name === "NotAllowedError" ? "Acc\xE8s au micro refus\xE9. Autorisez le micro dans les r\xE9glages du navigateur." : e.name === "NotFoundError" ? "Aucun micro d\xE9tect\xE9." : `Erreur micro : ${e.message}`;
      setError(reason);
      release2();
      return;
    }
    if (cancelDialRef.current) {
      stream.getTracks().forEach((t) => t.stop());
      release2();
      return;
    }
    try {
      const call = await client.dial(target.trim(), { mediaStream: stream });
      pendingDialRef.current = call;
      if (cancelDialRef.current) {
        try {
          call.hangup();
        } catch {
        }
        release2();
        return;
      }
      setActiveCall(wireActive(call));
      setError(null);
      release2();
    } catch (err) {
      setError(`dial: ${err.message}`);
      release2();
    }
  }
  async function dial() {
    await dialTo(number);
  }
  async function accept(call) {
    if (dialingRef.current) cancelDialing();
    if (activeCall && activeCall.call !== call) {
      try {
        activeCall.call.hangup();
      } catch {
      }
    }
    const outcome = await acceptCall({
      call,
      getMedia: () => navigator.mediaDevices.getUserMedia(micConstraints()),
      getIceServers: () => freshIceServersRef.current(),
      // plan 57 C-2 : la trace du décroché part vers l'hôte (→ plan de contrôle) — jamais bloquante
      onTrace: (tr) => {
        const since = incomingSinceRef.current.get(call);
        props.handlers.onAnswerEvent?.({
          ...tr,
          sipCallId: call.sipCallId ?? null,
          callerNumber: call.remoteIdentity ?? null,
          affaire: parseDisplayName(call.displayName).affaire,
          ringingForMs: since ? Date.now() - since : null
        });
      }
    });
    if (outcome.ok) {
      setError(null);
      setActiveCall(wireActive(call));
    } else if (outcome.kind === "gone") {
      setNotice(outcome.message);
    } else {
      setError(outcome.message);
    }
    setIncomingCalls((cur) => {
      const next = cur.filter((c) => c !== call);
      if (next.length === 0) ringer.stop();
      return next;
    });
  }
  function reject(call) {
    try {
      call.reject({ status: 486, reason: "Busy Here" });
    } catch {
    }
    setIncomingCalls((cur) => {
      const next = cur.filter((c) => c !== call);
      if (next.length === 0) ringer.stop();
      return next;
    });
  }
  function hangup() {
    activeCall?.call.hangup();
  }
  function pressDtmf(d) {
    if (!activeCall || activeCall.endedAt) return;
    try {
      activeCall.call.sendDtmf(d, { transport: "RFC2833" });
      setDtmfSent((s) => (s + d).slice(-32));
    } catch (err) {
      setError(`DTMF: ${err.message}`);
    }
  }
  useEffect(() => {
    setDtmfOpen(false);
    setDtmfSent("");
  }, [activeCall?.call.id]);
  function toggleMute() {
    if (!activeCall) return;
    const next = !activeCall.isMuted;
    activeCall.call.mute(next);
    setActiveCall({ ...activeCall, isMuted: next });
  }
  async function toggleHold() {
    if (!activeCall) return;
    try {
      if (activeCall.isOnHold) {
        await activeCall.call.resume();
        setActiveCall({ ...activeCall, isOnHold: false });
      } else {
        await activeCall.call.hold();
        setActiveCall({ ...activeCall, isOnHold: true });
      }
    } catch (err) {
      setError(`hold: ${err.message}`);
    }
  }
  function transferTo(sipUser) {
    if (!activeCall) return;
    try {
      activeCall.call.transfer(`sip:${sipUser}@${me.sip.domain}`);
      setTransferOpen(false);
    } catch (err) {
      setError(`transfer: ${err.message}`);
    }
  }
  function openNoteDraft(callId, current) {
    setNoteDrafts((cur) => ({ ...cur, [callId]: current }));
  }
  function cancelNoteDraft(callId) {
    setNoteDrafts((cur) => {
      const next = { ...cur };
      delete next[callId];
      return next;
    });
  }
  async function saveNoteDraft(callId, number2) {
    if (!handlers.setPhoneNote) return;
    const body = noteDrafts[callId] ?? "";
    setNoteSaving((cur) => ({ ...cur, [callId]: true }));
    try {
      const next = await handlers.setPhoneNote(number2, body);
      setCallerInfo((cur) => {
        const prev = cur[callId];
        if (!prev) return cur;
        return { ...cur, [callId]: { ...prev, note: next } };
      });
      cancelNoteDraft(callId);
    } catch (err) {
      setError(`note: ${err.message}`);
    } finally {
      setNoteSaving((cur) => {
        const next = { ...cur };
        delete next[callId];
        return next;
      });
    }
  }
  function appendDigit(d) {
    setNumber((n) => (n + d).slice(0, 32));
  }
  const elapsedSec = activeCall?.startedAt != null ? Math.max(0, Math.floor((Date.now() - activeCall.startedAt) / 1e3)) : 0;
  const others = presence.filter(
    (p) => p.sipUser !== me.sip.user && p.status !== "offline"
  );
  const availableOthers = others.filter(
    (p) => p.status === "available" || p.status === "away"
  );
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: `pkv-widget pkv-widget__container ${phase === "active" || phase === "incoming" ? "pkv-widget--wide" : ""} ${props.className ?? ""}`,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "pkv-header", children: [
          /* @__PURE__ */ jsxs("div", { className: "pkv-identity", children: [
            /* @__PURE__ */ jsx("span", { className: "pkv-identity__name", children: me.agent.name ?? me.sip.user }),
            /* @__PURE__ */ jsxs("span", { className: "pkv-identity__sub", children: [
              connectionState,
              " \xB7 ",
              registered ? "registered" : "unregistered"
            ] })
          ] }),
          /* @__PURE__ */ jsxs(
            "div",
            {
              className: "pkv-status-toggle",
              title: "Disponibilit\xE9 pour les appels entrants uniquement \u2014 vos coll\xE8gues peuvent toujours vous appeler en interne",
              children: [
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    className: myStatus === "available" ? "is-active" : "",
                    disabled: statusBusy || !registered,
                    onClick: () => void setStatus("available"),
                    type: "button",
                    title: "Je re\xE7ois les appels entrants (clients)",
                    children: "Disponible"
                  }
                ),
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    className: `${myStatus === "away" ? "is-active is-away" : ""}`,
                    disabled: statusBusy,
                    onClick: () => void setStatus("away"),
                    type: "button",
                    title: "Je ne re\xE7ois plus les appels entrants \u2014 le bureau peut toujours me joindre",
                    children: "Absent entrants"
                  }
                )
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsx("audio", { ref: audioRef, autoPlay: true, style: { display: "none" } }),
        /* @__PURE__ */ jsxs("div", { className: "pkv-body", children: [
          error && /* @__PURE__ */ jsx("div", { className: "pkv-error", children: error }),
          notice && !error && /* @__PURE__ */ jsx("div", { className: "pkv-notice", children: notice }),
          flows && flows.length > 0 && phase === "idle" && /* @__PURE__ */ jsxs("div", { className: "pkv-section pkv-flow-switcher", children: [
            /* @__PURE__ */ jsxs("div", { className: "pkv-flow-switcher__head", children: [
              /* @__PURE__ */ jsx("span", { className: "pkv-section__title", style: { margin: 0 }, children: "Flux d'appel" }),
              overrideFlowId ? /* @__PURE__ */ jsx("span", { className: "pkv-pill is-warn", children: "forc\xE9" }) : /* @__PURE__ */ jsx("span", { className: "pkv-pill is-success", children: "programmation" })
            ] }),
            handlers.setActiveOverride ? /* @__PURE__ */ jsxs("div", { className: "pkv-flow-switcher__list", role: "radiogroup", "aria-label": "Flux d'appel", children: [
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  role: "radio",
                  "aria-checked": !overrideFlowId,
                  className: `pkv-flow-switcher__pill${!overrideFlowId ? " pkv-flow-switcher__pill--active" : ""}`,
                  disabled: flowSwitchBusy || !overrideFlowId,
                  onClick: () => void applyOverride(null),
                  title: "Reprendre la programmation horaire automatique",
                  children: "\u{1F5D3} Auto"
                }
              ),
              flows.map((f) => /* @__PURE__ */ jsxs(
                "button",
                {
                  type: "button",
                  role: "radio",
                  "aria-checked": overrideFlowId === f.id,
                  className: `pkv-flow-switcher__pill${overrideFlowId === f.id ? " pkv-flow-switcher__pill--active" : ""}`,
                  disabled: flowSwitchBusy || overrideFlowId === f.id,
                  onClick: () => void applyOverride(f.id),
                  title: `Forcer \xAB ${f.name} \xBB${f.isDefault ? " \u2014 flux par d\xE9faut" : ""}`,
                  children: [
                    f.isDefault ? "\u2B50 " : "",
                    f.name
                  ]
                },
                f.id
              ))
            ] }) : /* @__PURE__ */ jsx("div", { className: "pkv-muted-text", children: overrideFlowId ? `Forc\xE9: ${flows.find((f) => f.id === overrideFlowId)?.name ?? "(actif)"}` : "Programmation auto" })
          ] }),
          incomingCalls.map((call) => {
            const parsed = parseDisplayName(call.displayName);
            return /* @__PURE__ */ jsxs("div", { className: "pkv-incoming", children: [
              (parsed.affaire || !isInternalCaller(call.remoteIdentity)) && /* @__PURE__ */ jsx(
                AffaireBanner,
                {
                  affaire: parsed.affaire,
                  onOpen: parsed.affaire && handlers.onOpenAffaire ? () => handlers.onOpenAffaire?.(parsed.affaire, call.id, call.remoteIdentity, call.sipCallId) : void 0
                }
              ),
              /* @__PURE__ */ jsxs("div", { className: "pkv-incoming__caller", children: [
                "\u{1F4DE} ",
                parsed.remoteName ?? call.remoteIdentity
              ] }),
              /* @__PURE__ */ jsx("div", { className: "pkv-muted-text", children: activeCall ? "Autre appel entrant" : "Appel entrant" }),
              (handlers.lookupCaller || handlers.getPhoneNote) && /* @__PURE__ */ jsx(
                CallerEnrichment,
                {
                  callId: call.id,
                  callerNumber: call.remoteIdentity,
                  info: callerInfo[call.id],
                  noteDraft: noteDrafts[call.id],
                  noteSaving: !!noteSaving[call.id],
                  canEditNote: !!handlers.setPhoneNote,
                  onOpenAffaire: handlers.onOpenAffaire,
                  onOpenNoteDraft: openNoteDraft,
                  onChangeNoteDraft: (id, body) => setNoteDrafts((cur) => ({ ...cur, [id]: body })),
                  onCancelNoteDraft: cancelNoteDraft,
                  onSaveNoteDraft: saveNoteDraft
                }
              ),
              /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: 6 }, children: [
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    className: "pkv-btn pkv-btn--primary pkv-btn--block",
                    onClick: () => void accept(call),
                    children: activeCall ? "D\xE9crocher (et raccrocher l'appel en cours)" : "Accepter"
                  }
                ),
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    className: "pkv-btn pkv-btn--danger pkv-btn--block",
                    onClick: () => reject(call),
                    children: "Refuser"
                  }
                )
              ] })
            ] }, call.id);
          }),
          phase === "active" && activeCall && /* @__PURE__ */ jsxs("div", { className: "pkv-active", children: [
            (activeCall.affaire || activeCall.call.direction === "inbound" && !isInternalCaller(activeCall.remoteIdentity)) && /* @__PURE__ */ jsx(
              AffaireBanner,
              {
                affaire: activeCall.affaire,
                onOpen: activeCall.affaire && handlers.onOpenAffaire ? () => handlers.onOpenAffaire?.(
                  activeCall.affaire,
                  activeCall.call.id,
                  activeCall.remoteIdentity,
                  activeCall.call.sipCallId
                ) : void 0
              }
            ),
            /* @__PURE__ */ jsx("div", { className: "pkv-active__caller", children: activeCall.remoteName ?? activeCall.remoteIdentity }),
            handlers.getSyncTarget && /* @__PURE__ */ jsxs("div", { className: "pkv-muted-text", style: { marginTop: 2 }, children: [
              syncTarget?.affaireId ? /* @__PURE__ */ jsxs(Fragment, { children: [
                "\u{1F399} Audio \u2192 dossier ",
                /* @__PURE__ */ jsxs("b", { children: [
                  "#",
                  syncTarget.number
                ] }),
                syncTarget.client ? ` \u2014 ${syncTarget.client}` : "",
                " ",
                /* @__PURE__ */ jsxs("i", { children: [
                  "(",
                  syncTarget.sourceLabel,
                  ")"
                ] })
              ] }) : /* @__PURE__ */ jsx(Fragment, { children: "\u26A0 Audio non li\xE9 \u2014 n\u2019ira dans aucun dossier" }),
              handlers.onSetSyncTarget && !syncEdit && /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: "pkv-btn pkv-btn--ghost",
                  style: { marginLeft: 6, padding: "0 6px", fontSize: 11 },
                  onClick: () => setSyncEdit(true),
                  children: "Modifier"
                }
              ),
              syncEdit && /* @__PURE__ */ jsxs("span", { className: "pkv-sync-edit", children: [
                /* @__PURE__ */ jsxs("span", { style: { display: "inline-flex", gap: 4, alignItems: "center" }, children: [
                  /* @__PURE__ */ jsx(
                    "input",
                    {
                      className: "pkv-input",
                      style: { width: searchSyncTargets ? 190 : 90, padding: "2px 6px", fontSize: 12 },
                      placeholder: searchSyncTargets ? "n\xB0 dossier, client, e-mail, adresse\u2026" : "n\xB0 dossier",
                      value: syncNum,
                      autoFocus: true,
                      onChange: (e) => setSyncNum(e.target.value),
                      onKeyDown: (e) => {
                        if (e.key === "Escape") {
                          setSyncEdit(false);
                          setSyncNum("");
                          setSyncHits([]);
                        }
                        if (e.key === "Enter" && searchSyncTargets && syncHits[0]) applySyncTarget(String(syncHits[0].number));
                      }
                    }
                  ),
                  !searchSyncTargets && /* @__PURE__ */ jsx(
                    "button",
                    {
                      type: "button",
                      className: "pkv-btn pkv-btn--primary",
                      style: { padding: "0 8px", fontSize: 11 },
                      onClick: () => applySyncTarget(syncNum.trim() || null),
                      children: "OK"
                    }
                  ),
                  syncTarget?.affaireId && /* @__PURE__ */ jsx(
                    "button",
                    {
                      type: "button",
                      className: "pkv-btn pkv-btn--ghost",
                      style: { padding: "0 6px", fontSize: 11 },
                      title: "Ne rattacher l'audio \xE0 aucun dossier",
                      onClick: () => applySyncTarget(null),
                      children: "D\xE9lier"
                    }
                  )
                ] }),
                searchSyncTargets && syncNum.trim().length >= 2 && /* @__PURE__ */ jsxs("ul", { className: "pkv-sync-results", role: "listbox", "aria-label": "Dossiers trouv\xE9s", children: [
                  syncHits.map((h) => /* @__PURE__ */ jsx("li", { role: "option", "aria-selected": false, children: /* @__PURE__ */ jsxs("button", { type: "button", className: "pkv-sync-results__item", onClick: () => applySyncTarget(String(h.number)), children: [
                    /* @__PURE__ */ jsxs("span", { className: "pkv-sync-results__main", children: [
                      "#",
                      h.number,
                      h.client ? ` \xB7 ${h.client}` : ""
                    ] }),
                    h.address && /* @__PURE__ */ jsx("span", { className: "pkv-sync-results__sub", children: h.address })
                  ] }) }, h.affaireId)),
                  !syncSearching && syncHits.length === 0 && /* @__PURE__ */ jsx("li", { className: "pkv-sync-results__empty", children: "Aucun dossier" }),
                  syncSearching && syncHits.length === 0 && /* @__PURE__ */ jsx("li", { className: "pkv-sync-results__empty", children: "Recherche\u2026" })
                ] })
              ] })
            ] }),
            /* @__PURE__ */ jsxs(
              "div",
              {
                className: `pkv-active__timer${activeCall.endedAt ? " pkv-active__timer--ended" : activeCall.startedAt ? "" : " pkv-active__timer--ringing"}`,
                children: [
                  activeCall.endedAt ? `\u2713 Appel termin\xE9${activeCall.startedAt ? ` \xB7 ${String(
                    Math.floor(
                      Math.max(
                        0,
                        (activeCall.endedAt - activeCall.startedAt) / 1e3
                      ) / 60
                    )
                  ).padStart(2, "0")}:${String(
                    Math.floor(
                      Math.max(
                        0,
                        (activeCall.endedAt - activeCall.startedAt) / 1e3
                      ) % 60
                    )
                  ).padStart(2, "0")}` : ""}` : activeCall.startedAt ? `${String(Math.floor(elapsedSec / 60)).padStart(2, "0")}:${String(
                    elapsedSec % 60
                  ).padStart(2, "0")}` : "\u{1F514} Appel en cours\u2026",
                  !activeCall.endedAt && activeCall.isOnHold && " \xB7 en attente",
                  !activeCall.endedAt && activeCall.isMuted && " \xB7 micro coup\xE9"
                ]
              }
            ),
            (handlers.lookupCaller || handlers.getPhoneNote) && /* @__PURE__ */ jsx(
              CallerEnrichment,
              {
                callId: activeCall.call.id,
                callerNumber: activeCall.remoteIdentity,
                info: callerInfo[activeCall.call.id],
                noteDraft: noteDrafts[activeCall.call.id],
                noteSaving: !!noteSaving[activeCall.call.id],
                canEditNote: !!handlers.setPhoneNote,
                onOpenAffaire: handlers.onOpenAffaire,
                onOpenNoteDraft: openNoteDraft,
                onChangeNoteDraft: (id, body) => setNoteDrafts((cur) => ({ ...cur, [id]: body })),
                onCancelNoteDraft: cancelNoteDraft,
                onSaveNoteDraft: saveNoteDraft
              }
            ),
            /* @__PURE__ */ jsxs("div", { className: "pkv-active__controls", children: [
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: "pkv-btn pkv-btn--ghost",
                  onClick: toggleMute,
                  disabled: !!activeCall.endedAt,
                  children: activeCall.isMuted ? "\u{1F508} Reprendre" : "\u{1F507} Couper"
                }
              ),
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: "pkv-btn pkv-btn--ghost",
                  onClick: () => void toggleHold(),
                  disabled: !!activeCall.endedAt,
                  children: activeCall.isOnHold ? "\u25B6 Reprendre" : "\u23F8 Attendre"
                }
              ),
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: `pkv-btn pkv-btn--ghost${dtmfOpen ? " is-active" : ""}`,
                  onClick: () => setDtmfOpen((o) => !o),
                  disabled: !!activeCall.endedAt,
                  title: "Clavier \u2014 envoyer des touches (serveurs vocaux)",
                  children: "\u2328 Clavier"
                }
              ),
              /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: "pkv-btn pkv-btn--ghost",
                  disabled: !!activeCall.endedAt || availableOthers.length === 0,
                  onClick: () => setTransferOpen(true),
                  children: "\u21C6 Transf\xE9rer"
                }
              )
            ] }),
            dtmfOpen && !activeCall.endedAt && /* @__PURE__ */ jsxs("div", { className: "pkv-dtmf", children: [
              /* @__PURE__ */ jsx("div", { className: "pkv-dtmf__echo", children: dtmfSent ? dtmfSent : /* @__PURE__ */ jsx("span", { className: "pkv-muted-text", children: "Touches envoy\xE9es\u2026" }) }),
              /* @__PURE__ */ jsx("div", { className: "pkv-dtmf__pad", children: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((d) => /* @__PURE__ */ jsx(
                "button",
                {
                  type: "button",
                  className: "pkv-dtmf__key",
                  onClick: () => pressDtmf(d),
                  children: d
                },
                d
              )) })
            ] }),
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                className: "pkv-btn pkv-btn--danger pkv-btn--block",
                onClick: hangup,
                disabled: !!activeCall.endedAt,
                children: activeCall.endedAt ? "Termin\xE9" : "Raccrocher"
              }
            )
          ] }),
          phase === "dialing" && /* @__PURE__ */ jsxs("div", { className: "pkv-section", children: [
            /* @__PURE__ */ jsxs("div", { className: "pkv-active__caller", children: [
              /* @__PURE__ */ jsx(PhoneGlyph, { mr: true }),
              " Appel en cours\u2026"
            ] }),
            /* @__PURE__ */ jsx("div", { className: "pkv-muted-text", children: number }),
            /* @__PURE__ */ jsx("button", { type: "button", className: "pkv-btn pkv-btn--block", onClick: cancelDialing, style: { marginTop: 6 }, children: "Annuler" })
          ] }),
          phase === "idle" && /* @__PURE__ */ jsxs(Fragment, { children: [
            !dialOpen && /* @__PURE__ */ jsx("div", { style: { display: "flex", flexDirection: "column", gap: 6 }, children: /* @__PURE__ */ jsxs(
              "button",
              {
                type: "button",
                className: "pkv-btn pkv-btn--primary pkv-btn--block",
                disabled: !registered,
                onClick: () => setDialOpen(true),
                children: [
                  /* @__PURE__ */ jsx(PhoneGlyph, { mr: true }),
                  " Appeler"
                ]
              }
            ) }),
            dialOpen && /* @__PURE__ */ jsxs("div", { className: "pkv-section pkv-dial", children: [
              /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between" }, children: [
                /* @__PURE__ */ jsx("h4", { className: "pkv-section__title", style: { margin: 0 }, children: "Dial" }),
                /* @__PURE__ */ jsx(
                  "button",
                  {
                    type: "button",
                    className: "pkv-btn pkv-btn--ghost",
                    style: { padding: "2px 8px", fontSize: 12 },
                    onClick: () => setDialOpen(false),
                    children: "\u2715"
                  }
                )
              ] }),
              /* @__PURE__ */ jsx(
                "input",
                {
                  type: "tel",
                  value: number,
                  onChange: (e) => setNumber(e.target.value),
                  placeholder: "+32\u2026",
                  disabled: !registered,
                  autoFocus: true
                }
              ),
              /* @__PURE__ */ jsx("div", { className: "pkv-dial-pad", children: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "0", "#"].map((d) => /* @__PURE__ */ jsx("button", { type: "button", disabled: !registered, onClick: () => appendDigit(d), children: d }, d)) }),
              /* @__PURE__ */ jsxs(
                "button",
                {
                  type: "button",
                  className: "pkv-btn pkv-btn--primary pkv-btn--block",
                  disabled: !registered || !number.trim(),
                  onClick: () => void dial(),
                  style: { marginTop: 6 },
                  children: [
                    /* @__PURE__ */ jsx(PhoneGlyph, { mr: true }),
                    " Call"
                  ]
                }
              )
            ] }),
            others.length > 0 && /* @__PURE__ */ jsxs("div", { className: "pkv-section", children: [
              /* @__PURE__ */ jsx("h4", { className: "pkv-section__title", children: "Autres agents" }),
              /* @__PURE__ */ jsx("div", { className: "pkv-presence", children: others.map((p) => {
                const callable = p.status !== "on-call" && p.status !== "offline" && !activeCall && registered;
                const tooltip = p.status === "on-call" ? `${p.sipUser} est en ligne` : p.status === "offline" ? `${p.sipUser} est hors ligne` : activeCall ? "Raccrochez l'appel actuel d'abord" : !registered ? "T\xE9l\xE9phone non enregistr\xE9" : p.status === "away" ? `Appeler ${p.sipUser} (absent entrants, joignable en interne)` : `Appeler ${p.sipUser}`;
                return /* @__PURE__ */ jsxs("div", { className: "pkv-presence__row", children: [
                  /* @__PURE__ */ jsx("span", { className: "pkv-presence__name", children: p.sipUser }),
                  /* @__PURE__ */ jsx("span", { className: `pkv-pill ${pillClass(p.status)}`, children: statusLabel(p.status) }),
                  /* @__PURE__ */ jsx(
                    "button",
                    {
                      type: "button",
                      className: "pkv-btn pkv-btn--primary",
                      disabled: !callable,
                      onClick: () => void dialTo(p.sipUser),
                      title: tooltip,
                      "aria-label": tooltip,
                      children: /* @__PURE__ */ jsx(PhoneGlyph, { size: 15 })
                    }
                  )
                ] }, p.sipUser);
              }) })
            ] })
          ] })
        ] }),
        transferOpen && activeCall && /* @__PURE__ */ jsxs("div", { className: "pkv-transfer-overlay", children: [
          /* @__PURE__ */ jsx("div", { className: "pkv-section__title", children: "Transf\xE9rer vers\u2026" }),
          availableOthers.length === 0 && /* @__PURE__ */ jsx("div", { className: "pkv-muted-text", children: "Aucun agent disponible pour le moment." }),
          availableOthers.map((p) => /* @__PURE__ */ jsxs(
            "button",
            {
              type: "button",
              className: "pkv-btn pkv-btn--primary pkv-btn--block",
              onClick: () => transferTo(p.sipUser),
              children: [
                p.sipUser,
                p.status === "away" ? " \xB7 absent entrants" : ""
              ]
            },
            p.sipUser
          )),
          /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "pkv-btn pkv-btn--ghost pkv-btn--block",
              onClick: () => setTransferOpen(false),
              children: "Annuler"
            }
          )
        ] })
      ]
    }
  );
}
function pillClass(status) {
  if (status === "available") return "is-success";
  if (status === "on-call") return "is-warn";
  if (status === "away") return "";
  return "is-danger";
}
function statusLabel(status) {
  if (status === "available") return "disponible";
  if (status === "on-call") return "en appel";
  if (status === "away") return "absent entrants";
  return "hors ligne";
}
function CallerEnrichment(props) {
  const { callId, callerNumber, info } = props;
  if (!info) return /* @__PURE__ */ jsx(Fragment, {});
  if (info.loading) {
    return /* @__PURE__ */ jsx("div", { className: "pkv-caller-info pkv-caller-info--loading", children: /* @__PURE__ */ jsx("span", { className: "pkv-muted-text", children: "Recherche du contact\u2026" }) });
  }
  const editing = props.noteDraft !== void 0;
  const note = info.note;
  return /* @__PURE__ */ jsxs("div", { className: "pkv-caller-info", children: [
    info.lookup && info.lookup.matches.length > 0 ? info.lookup.matches.map((m) => /* @__PURE__ */ jsxs("div", { className: "pkv-caller-match", children: [
      /* @__PURE__ */ jsxs("div", { className: "pkv-caller-match__head", children: [
        /* @__PURE__ */ jsx("strong", { children: m.name }),
        m.email && /* @__PURE__ */ jsxs("span", { className: "pkv-muted-text", children: [
          " \xB7 ",
          m.email
        ] })
      ] }),
      m.affaires.length === 0 ? /* @__PURE__ */ jsx("div", { className: "pkv-muted-text", style: { fontSize: 11 }, children: "Aucune affaire active" }) : /* @__PURE__ */ jsx("ul", { className: "pkv-caller-affaires", children: m.affaires.map((a) => /* @__PURE__ */ jsxs("li", { className: "pkv-caller-affaire", children: [
        /* @__PURE__ */ jsxs("div", { className: "pkv-caller-affaire__line", children: [
          /* @__PURE__ */ jsxs("span", { className: "pkv-pill is-warn", children: [
            "Affaire ",
            a.id
          ] }),
          a.role && /* @__PURE__ */ jsxs("span", { className: "pkv-muted-text", children: [
            " \xB7 ",
            a.role
          ] }),
          props.onOpenAffaire && /* @__PURE__ */ jsx(
            "button",
            {
              type: "button",
              className: "pkv-btn pkv-btn--ghost",
              style: {
                padding: "2px 8px",
                fontSize: 11,
                marginLeft: "auto"
              },
              onClick: () => props.onOpenAffaire?.(a.id, props.callId, props.callerNumber),
              children: "Ouvrir \u2197"
            }
          )
        ] }),
        (a.address || a.status) && /* @__PURE__ */ jsxs("div", { className: "pkv-caller-affaire__meta", children: [
          a.address && /* @__PURE__ */ jsx("span", { children: a.address }),
          a.address && a.status && /* @__PURE__ */ jsx("span", { children: " \xB7 " }),
          a.status && /* @__PURE__ */ jsx("span", { children: a.status })
        ] })
      ] }, a.id)) })
    ] }, m.contactId)) : /* @__PURE__ */ jsx("div", { className: "pkv-muted-text", style: { fontSize: 11 }, children: "Num\xE9ro inconnu de la base." }),
    /* @__PURE__ */ jsx("div", { className: "pkv-caller-note", children: !editing ? /* @__PURE__ */ jsxs(Fragment, { children: [
      note ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs("div", { className: "pkv-caller-note__body", children: [
          "\u{1F4DD} ",
          note.body
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "pkv-caller-note__meta", children: [
          note.updatedByName ? `${note.updatedByName} \xB7 ` : "",
          formatRelative(note.updatedAt)
        ] })
      ] }) : /* @__PURE__ */ jsx("div", { className: "pkv-muted-text", style: { fontSize: 11 }, children: "Aucune note pour ce num\xE9ro." }),
      props.canEditNote && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          className: "pkv-btn pkv-btn--ghost",
          style: { padding: "2px 8px", fontSize: 11, marginTop: 4 },
          onClick: () => props.onOpenNoteDraft(callId, note?.body ?? ""),
          children: note ? "\u270E Modifier la note" : "+ Ajouter une note"
        }
      )
    ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        "textarea",
        {
          className: "pkv-caller-note__textarea",
          rows: 2,
          value: props.noteDraft ?? "",
          onChange: (e) => props.onChangeNoteDraft(callId, e.target.value),
          placeholder: "ex. Client habituel, parle fran\xE7ais\u2026",
          autoFocus: true
        }
      ),
      /* @__PURE__ */ jsxs("div", { className: "pkv-caller-note__actions", children: [
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "pkv-btn pkv-btn--primary",
            style: { padding: "2px 8px", fontSize: 11 },
            disabled: props.noteSaving,
            onClick: () => void props.onSaveNoteDraft(callId, callerNumber),
            children: props.noteSaving ? "\u2026" : "Enregistrer"
          }
        ),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            className: "pkv-btn pkv-btn--ghost",
            style: { padding: "2px 8px", fontSize: 11 },
            disabled: props.noteSaving,
            onClick: () => props.onCancelNoteDraft(callId),
            children: "Annuler"
          }
        )
      ] })
    ] }) })
  ] });
}
function formatRelative(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return iso;
  }
}

export { VoipWidget };
//# sourceMappingURL=index.mjs.map
//# sourceMappingURL=index.mjs.map